import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RotateCcw, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import { projectApi } from '@/api/projectApi'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuthStore } from '@/stores/authStore'
import type { Project } from '@/types/nf'

export const TRASH_PAGE_SIZE = 12

/** 按标题模糊筛选 + 按删除时间倒序 (最近删除在前)。纯函数, 便于测试。 */
export function filterTrashProjects(projects: Project[], search: string): Project[] {
  const q = search.trim().toLowerCase()
  const matched = q ? projects.filter((p) => p.title.toLowerCase().includes(q)) : projects
  return [...matched].sort((a, b) => (b.deleted_at ?? 0) - (a.deleted_at ?? 0))
}

export function paginateTrashProjects(projects: Project[], page: number, pageSize = TRASH_PAGE_SIZE): Project[] {
  const start = page * pageSize
  return projects.slice(start, start + pageSize)
}

function formatDeletedAt(value?: number | null) {
  if (!value) return ''
  return new Date(value * 1000).toLocaleString()
}

export default function ProjectTrash() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [purgeTarget, setPurgeTarget] = useState<Project | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  const trashQuery = useQuery({
    queryKey: ['projects-trash', user?.user_id],
    queryFn: () => projectApi.trash(user!.user_id),
    enabled: Boolean(user?.user_id),
  })

  const projects = useMemo(() => trashQuery.data ?? [], [trashQuery.data])
  const filteredProjects = useMemo(() => filterTrashProjects(projects, search), [projects, search])
  const pageCount = Math.max(1, Math.ceil(filteredProjects.length / TRASH_PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const pagedProjects = useMemo(() => paginateTrashProjects(filteredProjects, safePage), [filteredProjects, safePage])

  const restoreMutation = useMutation({
    mutationFn: (project: Project) => projectApi.restore(project.id),
    onSuccess: async () => {
      toast.success(t('project.trash.restoreSuccess'))
      await queryClient.invalidateQueries({ queryKey: ['projects'] })
      await queryClient.invalidateQueries({ queryKey: ['projects-trash'] })
    },
    onError: (error) => toast.error(friendlyMessage(error, t('project.trash.restoreFailed'))),
  })

  const purgeMutation = useMutation({
    mutationFn: (project: Project) => projectApi.purge(project.id),
    onSuccess: async () => {
      setPurgeTarget(null)
      toast.success(t('project.trash.purgeSuccess'))
      await queryClient.invalidateQueries({ queryKey: ['projects'] })
      await queryClient.invalidateQueries({ queryKey: ['projects-trash'] })
    },
    onError: (error) => toast.error(friendlyMessage(error, t('project.trash.purgeFailed'))),
  })

  return (
    <section className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal sm:text-3xl">{t('project.trash.title')}</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t('project.trash.subtitle')}</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/projects">{t('project.backToProjects')}</Link>
        </Button>
      </div>

      {trashQuery.error ? (
        <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {friendlyMessage(trashQuery.error, t('common.serviceError'))}
        </div>
      ) : null}

      {trashQuery.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : projects.length ? (
        <div className="space-y-4">
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(0)
            }}
            placeholder={t('project.trash.searchPlaceholder')}
            className="max-w-sm"
            aria-label={t('project.trash.searchPlaceholder')}
          />
          {filteredProjects.length ? (
            <>
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('project.name')}</TableHead>
                <TableHead>{t('project.trash.deletedAt')}</TableHead>
                <TableHead>{t('project.trash.remainingDays')}</TableHead>
                <TableHead className="text-right">{t('project.trash.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedProjects.map((project) => (
                <TableRow key={project.id}>
                  <TableCell className="font-medium">{project.title}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDeletedAt(project.deleted_at)}</TableCell>
                  <TableCell className="nf-mono">{t('project.trash.remainingDaysValue', { count: project.remaining_days ?? 0 })}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => restoreMutation.mutate(project)}
                        disabled={restoreMutation.isPending || purgeMutation.isPending}
                      >
                        <RotateCcw className="size-4" />
                        {t('project.trash.restore')}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setPurgeTarget(project)}
                        disabled={restoreMutation.isPending || purgeMutation.isPending}
                      >
                        <Trash2 className="size-4" />
                        {t('project.trash.purge')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
              <Pagination
                page={safePage}
                pageCount={pageCount}
                pageSize={TRASH_PAGE_SIZE}
                total={filteredProjects.length}
                onPage={setPage}
              />
            </>
          ) : (
            <div className="grid min-h-[200px] place-items-center rounded-lg border border-dashed bg-card/45 p-8 text-center">
              <p className="text-sm text-muted-foreground">{t('project.trash.noMatch')}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="grid min-h-[320px] place-items-center rounded-lg border border-dashed bg-card/45 p-8 text-center">
          <div>
            <p className="text-lg font-medium">{t('project.trash.empty')}</p>
            <p className="mt-2 text-sm text-muted-foreground">{t('project.trash.emptyHint')}</p>
          </div>
        </div>
      )}

      <Dialog open={Boolean(purgeTarget)} onOpenChange={(nextOpen) => {
        if (!nextOpen && !purgeMutation.isPending) setPurgeTarget(null)
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('project.trash.purgeConfirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('project.trash.purgeConfirmDescription', { title: purgeTarget?.title ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeTarget(null)} disabled={purgeMutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => purgeTarget && purgeMutation.mutate(purgeTarget)}
              disabled={purgeMutation.isPending}
            >
              <Trash2 className="size-4" />
              {t('project.trash.purge')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
